import type { CreateEntryInput, Entry, EntryKind, ProposalInput, Visibility } from "./api";

/**
 * Le formulaire d'un point, et le corps qu'il envoie. Fonctions pures, testees a part.
 *
 * Les dates partent **en texte seul** (`startRaw`, `endRaw`) : le serveur les lit avec la date de
 * naissance du proprietaire (ticket 3). Le corps est complet a chaque fois, creation comme
 * modification : une modification qui envoie un nouveau texte sans champ structure le fait relire,
 * et les bornes qui disparaissent (moment, « en cours », « a dater ») partent a `null`.
 *
 * `proposalBody` en retire la visibilite et les proches nommes : c'est le corps d'une proposition
 * d'un proche (ticket 7), que le serveur refuse avec ces champs.
 */
export type EntryForm = {
  kind: EntryKind;
  title: string;
  trackId: number | null;
  startRaw: string;
  endRaw: string;
  ongoing: boolean;
  note: string;
  visibility: Visibility;
  /** Les proches nommes, pour « Des proches choisis » (ticket 7). */
  viewers: number[];
};

/** Le formulaire vide (nouveau point) ou rempli depuis un point existant. */
export function formFromEntry(entry: Entry | null, defaultTrackId: number | null): EntryForm {
  if (entry === null) {
    return {
      kind: "point",
      title: "",
      trackId: defaultTrackId,
      startRaw: "",
      endRaw: "",
      ongoing: false,
      note: "",
      visibility: "private",
      viewers: [],
    };
  }
  return {
    kind: entry.kind,
    title: entry.title,
    trackId: entry.trackId,
    startRaw: entry.undated ? "" : (entry.startRaw ?? ""),
    endRaw: entry.endRaw ?? "",
    ongoing: entry.ongoing,
    note: entry.note ?? "",
    visibility: entry.visibility,
    viewers: entry.viewers ?? [],
  };
}

/** Ce qui empeche d'enregistrer, en francais, ou `null`. */
export function formIssue(form: EntryForm): string | null {
  if (form.title.trim() === "") return "Donne un titre à ce moment.";
  if (form.trackId === null) return "Choisis une piste.";
  if (
    form.kind === "period" &&
    form.startRaw.trim() !== "" &&
    !form.ongoing &&
    form.endRaw.trim() === ""
  ) {
    return "Dis jusqu'à quand, ou coche « en cours ».";
  }
  if (form.visibility === "custom" && form.viewers.length === 0) {
    return "Choisis au moins un proche, ou « Moi seul ».";
  }
  return null;
}

const NO_START = { startRaw: null, startFrom: null, startTo: null, startPrecision: null } as const;
const NO_END = { endRaw: null, endFrom: null, endTo: null, endPrecision: null } as const;

/**
 * Le corps a envoyer. Sans « Quand ? », le point part « a dater ». A n'appeler qu'apres
 * `formIssue` : une piste absente leve.
 */
export function entryBody(form: EntryForm): CreateEntryInput {
  if (form.trackId === null) throw new Error("piste absente");
  const note = form.note.trim();
  const base = {
    kind: form.kind,
    title: form.title.trim(),
    trackId: form.trackId,
    note: note === "" ? null : note,
    visibility: form.visibility,
    // Les proches nommes ne partent qu'avec « Des proches choisis » : le serveur efface les noms
    // d'un point qui quitte cette visibilite.
    ...(form.visibility === "custom" ? { viewers: form.viewers } : {}),
  };
  const start = form.startRaw.trim();
  if (start === "") return { ...base, ...NO_START, ...NO_END, undated: true, ongoing: false };
  const dated = { ...base, startRaw: start, undated: false };
  if (form.kind === "point" || form.ongoing) {
    return { ...dated, ...NO_END, ongoing: form.kind === "period" };
  }
  // Surtout pas `endFrom: null` a cote du texte : une modification ne relit un texte que si aucun
  // champ structure de sa borne n'est envoye, `null` compris.
  return { ...dated, endRaw: form.endRaw.trim(), ongoing: false };
}

/** Le corps d'une proposition : celui d'un point, sans visibilite ni proches nommes. */
export function proposalBody(form: EntryForm): ProposalInput {
  const { visibility: _visibility, viewers: _viewers, ...body } = entryBody(form);
  return body;
}
